import {
	DownloadCloud01,
	File04,
	Trash03,
} from "@untitled-ui/icons-react";
import { AlertCircleIcon } from "lucide-react";
import { useEffect } from "react";
import {
	formatBytes,
	useFileUpload,
} from "../../hooks/use-file-upload";
import { Button } from "../ui/button";

interface UploadMultipleFilesProps {
	onFilesChange?: (files: File[]) => void;
	subText?: string;
}

export const UploadMultipleFiles = ({
	onFilesChange,
	subText,
}: UploadMultipleFilesProps) => {
	const maxSize = 10 * 1024 * 1024; // 10MB
	const [
		{ files, isDragging, errors },
		{
			handleDragEnter,
			handleDragLeave,
			handleDragOver,
			handleDrop,
			openFileDialog,
			removeFile,
			getInputProps,
		},
	] = useFileUpload({ maxSize, multiple: true });

	useEffect(() => {
		const fileObjects = files
			.map((fileWithPreview) => fileWithPreview.file)
			.filter((file): file is File => file instanceof File);
		onFilesChange?.(fileObjects);
	}, [files, onFilesChange]);

	return (
		<div className="flex flex-col gap-2">
			<div
				role="button"
				onClick={openFileDialog}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				data-dragging={isDragging || undefined}
				className="border-input hover:bg-accent/50 data-[dragging=true]:bg-accent/50 has-[input:focus]:border-ring has-[input:focus]:ring-ring/50 flex min-h-40 flex-col items-center justify-center rounded-xl border border-dashed p-6 transition-colors cursor-pointer"
			>
				<input
					{...getInputProps()}
					className="inset-0 w-full h-full opacity-0 cursor-pointer"
					aria-label="Upload files"
				/>

				<div>
					<span className="inline-block bg-gray-100 p-3 rounded-full">
						<DownloadCloud01 className="w-6 h-6" />
					</span>
				</div>
				<span className="text-base mt-2 text-center">
					<span className="text-text-blue font-semibold">
						Cliquez pour ajouter
					</span>{" "}
					ou drag and drop{" "}
					{subText ? (
						<span>{subText}</span>
					) : (
						<span>(max. {formatBytes(maxSize)})</span>
					)}
				</span>
			</div>

			{errors.length > 0 && (
				<div
					className="text-destructive flex items-center gap-1 text-sm"
					role="alert"
				>
					<AlertCircleIcon className="size-4 shrink-0" />
					<span>{errors[0]}</span>
				</div>
			)}

			{files.length > 0 && (
				<div className="flex flex-col gap-2">
					{files.map((fileWithPreview) => (
						<div
							key={fileWithPreview.id}
							className="flex items-center justify-between gap-4 border border-dashed rounded-xl px-4 py-3 bg-white min-w-0"
						>
							<div className="flex items-center gap-3 min-w-0">
								<File04 className="w-5 h-5 shrink-0" />
								<div className="min-w-0">
									<div className="font-medium text-sm truncate">
										{fileWithPreview.file.name}
									</div>
									<div className="text-xs text-gray-500">
										{formatBytes(fileWithPreview.file.size)}
									</div>
								</div>
							</div>
							<Button
								type="button"
								size="icon"
								variant="ghost"
								onClick={(event) => {
									event.stopPropagation();
									removeFile(fileWithPreview.id);
								}}
								className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive rounded-lg ring-1 ring-input hover:bg-accent"
							>
								<Trash03 className="size-4" />
							</Button>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
